import { useCallback, useState, useMemo } from 'react';
import { useRouter } from '@bymason-private/route-provider';
import {
  Box,
  Button,
  ControllerButtonMenu,
  ControllerMenuItem,
} from '@bymason-private/legacy-pattern-components';
import {
  useToast,
  DEPLOY_TYPE_MAP,
  DEFAULT_GROUP_NAME,
  GroupDetailsView,
} from '@bymason-private/controller-shared-widgets';
import { ArtifactDeployment, ArtifactType } from '@bymason/controller-api-hooks';

import Layout from '../../common/layout';
import { routes } from '../../router';
import { useCheckFeatureFlag } from '../../global-state';

export default function GroupView({ name = '' }: GroupViewProps) {
  const { navigate } = useRouter();
  const [setToastMessage, setToastSeverity] = useToast();
  const [isBuildEnabled] = useCheckFeatureFlag('controller_build');

  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState<ArtifactType | null>(null);
  const [deviceCount, setDeviceCount] = useState<number>();

  const isDefaultGroup = useMemo(() => name.toLowerCase() === DEFAULT_GROUP_NAME, [name]);

  const handleGroupDetailsLoad = (_: unknown, count?: number) => {
    setDeviceCount(count);
  };

  const handleViewDevices = useCallback(() => {
    navigate(routes.Devices, { group: name }, { addUnusedParamsToSearch: true });
  }, [name, navigate]);

  const handleDeployModalOpen = (type: ArtifactType) => () => {
    setShowDeployModal(type);
  };

  const handleDeployModalClose = () => {
    setShowDeployModal(null);
  };

  const handleDeploySuccess = useCallback(
    (deploy: ArtifactDeployment) => {
      setToastSeverity('success');
      setToastMessage(`${deploy.artifactName} deployment issued to ${name}`);
    },
    [name, setToastMessage, setToastSeverity]
  );

  const handleNewProjectClick = () => {
    navigate(
      routes.Projects,
      { create: true },
      {
        addUnusedParamsToSearch: true,
      }
    );
  };

  const handleDeleteGroupModalOpen = () => {
    setShowDeleteGroupModal(true);
  };

  const handleDeleteGroupModalClose = () => {
    setShowDeleteGroupModal(false);
  };

  const handleDeleteGroupSuccess = useCallback(
    (groupName) => {
      setShowDeleteGroupModal(false);
      setToastSeverity('success');
      setToastMessage(`Successfully deleted group "${groupName}"`);
      navigate(routes.Groups);
    },
    [navigate, setToastMessage, setToastSeverity]
  );

  const handleErrors = useCallback(
    (errorMessage: string) => {
      if (errorMessage) {
        setToastSeverity('error');
        setToastMessage(errorMessage);
      }
    },
    [setToastSeverity, setToastMessage]
  );
  return (
    <Layout
      // busy={groupDetailsBusy || deploymentsBusy}
      headerText={name}
      headerRightAdornment={
        <Box display='flex' columnGap={1}>
          <Button
            variant='outlined'
            onClick={handleDeleteGroupModalOpen}
            disabled={isDefaultGroup || deviceCount !== 0}
          >
            Delete group
          </Button>
          <Button variant='outlined' onClick={handleViewDevices}>
            {`View devices${deviceCount === undefined ? '' : ` (${deviceCount})`}`}
          </Button>
          {!isDefaultGroup && (
            <ControllerButtonMenu variant='outlined' buttonText='Deploy to group'>
              {[...DEPLOY_TYPE_MAP].map(([type, text]) => (
                <ControllerMenuItem
                  key={type}
                  onClick={handleDeployModalOpen(type as ArtifactType)}
                  disabled={(type === ArtifactType.Apk || type === ArtifactType.Ota) && !deviceCount}
                >
                  {text.charAt(0).toUpperCase() + text.slice(1)} ({type})
                </ControllerMenuItem>
              ))}
            </ControllerButtonMenu>
          )}
        </Box>
      }
      stickyHeader
    >
      <GroupDetailsView
        name={name}
        groupsViewRoute={routes.Groups}
        projectViewRoute={routes.Project}
        controllerBuildEnabled={isBuildEnabled}
        showDeleteGroupModal={showDeleteGroupModal}
        onCloseDeleteGroupModal={handleDeleteGroupModalClose}
        onDeleteGroupSuccess={handleDeleteGroupSuccess}
        onDeleteGroupError={handleErrors}
        showDeployModal={showDeployModal}
        onCloseDeployModal={handleDeployModalClose}
        onDeploySuccess={handleDeploySuccess}
        onCreateNewProject={handleNewProjectClick}
        onGroupDetailsLoaded={handleGroupDetailsLoad}
      />
    </Layout>
  );
}

interface GroupViewProps {
  name?: string;
}
